// AI Generated Component
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { DomainRepoService } from '@client/app/services/api/domain-repo/domain-repo.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { StateService } from '@uirouter/core';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { LoginService } from '@client/app/core/services/login/login.service';
import { ExtensionService } from '@client/app/modules/extension/services/extension.service';
import { IntercomService } from '@client/app/modules/chat/intercom/services/intercom.service';
import { DomainGroupService } from '@client/app/services/entities/domain-group/domain-group.service';
import { BehaviorSubject } from 'rxjs';
import { environment, version, activeFeatures, proxyApiPath } from '@monsido/core/constants/general.constant';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { FormDomainComponent } from '@client/app/forms/domain/form-domain.component';
import { MON_EVENTS } from '@monsido/core/constants/mon-events.constant';
import { Domain } from '@monsido/modules/models/api/domain';
import { MonEventService } from '@monsido/services/mon-event/mon-event.service';
import { Customer } from '@monsido/modules/models/api/customer';
import { User } from '@monsido/modules/models/api/user';

@Component({
    selector: 'dashboard',
    standalone: false,
    templateUrl: './dashboard.component.html',
})
export class DashboardComponent extends BaseContainerComponentController implements OnInit, OnDestroy {
    promise: any = null;
    domainObservable: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    subheaderTranslation: string = '';
    chatSystem: string | undefined = '';
    environment: string = environment;
    version: string = version;
    hasExtension: boolean | null = null;
    showVersion: boolean = false;
    domainCount: number = 0;
    monsidoPath: string = '';
    allLabelsMap: any = {};
    timerPromise: any = null;
    override labels: any[] = [];
    override showLabels = activeFeatures.labels;
    override selectedLabels: any[] = [];
    override showFilters = activeFeatures.table_filters;
    override availableFilters: any = {
        labels: [],
    };
    override isFiltersOpen = false;
    override filters: any = {
        labels: [],
    };
    override labelsPromise: Promise<void> | null = null;
    customer: Customer | null = null;
    user: User | null | undefined = null;

    private intervalSubscription: any = null;

    constructor (
        private domainRepoService: DomainRepoService,
        private translateService: TranslateService,
        private stateService: StateService,
        private paramService: ParamService,
        private loginService: LoginService,
        private extensionService: ExtensionService,
        private intercomService: IntercomService,
        private domainGroupService: DomainGroupService,
        private cdRef: ChangeDetectorRef,
        private monEventService: MonEventService,
    ) {
        super({ table: true });
        this.currentSort.by = 'favorite';
        this.currentSort.direction = 'asc';
    }

    ngOnDestroy (): void {
        this.destroyTimer();
    }

    ngOnInit (): void {
        this.customer = this.sessionService.customer;
        this.user = this.sessionService.me;
        this.subheaderTranslation = '';
        this.chatSystem = this.sessionService.getChatSystem();
        this.search = '';
        this.hasExtension = null;
        this.domainCount = 0;
        this.hasExtension = null;
        this.domainObservable = new BehaviorSubject<Domain[]>([]);
        this.getLabels();
        this.loadLocations();
        this.currentSort.by = 'favorite';
        this.currentSort.direction = 'asc';
        this.getPage(true);

        this.startAutoReload();
        this.monsidoPath = proxyApiPath;

        this.extensionService.isInstalled().then(res => {
            this.hasExtension = res;
        });
    }

    isIntercomChat (): boolean {
        return this.chatSystem === 'intercom';
    }

    sendIntercomMsg (msg: string): void {
        this.intercomService.addMsg(msg);
    }

    loadLocations (): void {
        const params = this.stateService.params;
        if (typeof params.page !== 'undefined') {
            this.page = params.page;
        }

        if (typeof params.page_size !== 'undefined') {
            this.pageSize = params.page_size;
        }

        if (typeof params.search !== 'undefined') {
            this.search = params.search;
        }

        if (typeof params.sort_by !== 'undefined') {
            this.currentSort.by = params.sort_by;
        }

        if (typeof params.sort_dir !== 'undefined') {
            this.currentSort.direction = params.sort_dir;
        }
    }

    getSubHeaderDescription (): void {
        const params = {
            domains: this.domainCount,
        };
        this.subheaderTranslation = this.translateService.getString(
            'You have [[domains]] domain(s) on your account',
            params,
        );
    }

    destroyTimer (): void {
        if (this.intervalSubscription) {
            clearInterval(this.intervalSubscription);
        }
    }

    startAutoReload (): void {
        this.intervalSubscription = setInterval(() => {
            this.getPage(false);
        }, 30000);
    }

    override async getLabels (): Promise<any> {
        try {
            const data = await this.labelRepo.getLabels();
            this.labels = data || [];
            this.availableFilters.labels = data || [];
            this.allLabelsMap = data.reduce((acc: any, label: any) => {
                acc[label.id] = label;
                return acc;
            }, {});
            return this.allLabelsMap;
        } catch (e) {
            return {};
        }
    }

    override getPage = (returnPromise = true): void => {
        const params: any = {
            page_size: this.pageSize,
            page: this.page,
            sort_by: this.currentSort.by,
            sort_dir: this.currentSort.direction,
            search: this.search || null,
        };
        if (this.selectedLabels && this.selectedLabels.length > 0) {
            params['labels[]'] = this.selectedLabels;
        }

        this.paramService.setParams({
            page_size: params.page_size.toString(),
            page: params.page.toString(),
            sort_by: params.sort_by,
            sort_dir: params.sort_dir,
            search: params.search || '',
        });

        const promise = this.domainRepoService.getAll(params).then((domains: any) => {
            this.domainCount = domains.total;
            this.getSubHeaderDescription();
            for (let i = 0; i < domains.length; i++) {
                const domainGroups = domains[i].domain_groups;

                if (typeof domains[i].crawl_history === 'object' && domains[i].crawl_history !== null) {
                    domains[i].crawl_history.qa_count =
                        domains[i].crawl_history.dead_links_count +
                        domains[i].crawl_history.dead_images_count +
                        domains[i].crawl_history.spelling_errors_confirmed_count;

                    domains[i].crawl_history.seo_count =
                        domains[i].crawl_history.seo_errors_alerts_count +
                        domains[i].crawl_history.seo_errors_infos_count +
                        domains[i].crawl_history.seo_errors_warnings_count;
                }

                domainGroups.forEach((group: any) => {
                    group.labels = this.domainGroupService.getDomainGroupLabels(group, this.allLabelsMap);
                });
            }

            this.domainObservable?.next(domains);
        }).catch((e) => {
            // Handle error
            // eslint-disable-next-line
            console.error('Error fetching domains:', e);
        });

        if (returnPromise) {
            this.promise = promise;
        }
    };

    onFiltersChanges (changes: any): void {
        this.page = 1;
        this.updateFilters(changes);
        this.getPage();
    }

    override updateFilters (changes) {
        if (changes.changes) {
            changes = changes.changes;
        }

        if (changes.labels) {
            this.selectedLabels = changes.labels.map((label) => {
                return label.id;
            });
        } else {
            this.selectedLabels = [];
        }
    }

    createDomain (domain?: Domain) {
        const params = {
            body: FormDomainComponent,
            size: 'md',
            data: { domain: domain },
            cdRef: this.cdRef,
        };

        const options = {
            params,
            callback: (updatedDomain: any) => {
                if (updatedDomain) {
                    this.getPage();
                }
            },
        };

        this.monEventService.run(MON_EVENTS.LOAD_DIALOG, options);
    }
}
